import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL } from "@/constants/site";
import { ADMIN } from "@/constants/testIds";
import Seo from "@/components/Seo";

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100svh] bg-[var(--bg)] text-[var(--text)] flex flex-col">
      <Seo title="Admin Login" description="Admin portal login." path="/admin/login" noindex />
      <div className="max-w-[1600px] w-full mx-auto px-6 md:px-10 pt-10">
        <img src={LOGO_URL} alt="Magee" className="h-12 md:h-14" />
      </div>
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center justify-center px-6 md:px-16 py-20"
        >
          <form onSubmit={submit} className="w-full max-w-md">
            <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--accent)]">
              Access · Restricted
            </span>
            <h1 className="mt-6 font-editorial text-5xl md:text-6xl leading-[0.95] tracking-tighter">
              Agency
              <br />
              <em className="text-[var(--accent)]">console.</em>
            </h1>
            <p className="mt-6 text-[var(--text)]/60">Sign in to view submissions.</p>

            <div className="mt-12 space-y-8">
              <label className="block">
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Email
                </span>
                <input
                  type="email"
                  required
                  data-testid={ADMIN.emailInput}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-underline"
                  autoComplete="email"
                />
              </label>
              <label className="block">
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]/50">
                  Password
                </span>
                <input
                  type="password"
                  required
                  data-testid={ADMIN.passwordInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-underline"
                  autoComplete="current-password"
                />
              </label>
            </div>

            {error && (
              <p
                data-testid={ADMIN.error}
                className="mt-6 text-sm text-red-400 font-mono-brand"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid={ADMIN.submit}
              className="mt-12 inline-flex items-center gap-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-60 text-[var(--text)] px-8 py-4 font-mono-brand text-[11px] uppercase tracking-[0.22em] transition-all"
            >
              <LogIn size={14} />
              {submitting ? "Signing in…" : "Sign In"}
              <ArrowUpRight size={14} />
            </button>
          </form>
        </motion.div>

        <div className="hidden lg:block relative overflow-hidden border-l border-[var(--text)]/10">
          <img
            src="https://images.unsplash.com/photo-1766019463451-04e0b1269f37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt=""
            className="absolute inset-0 w-full h-full object-cover grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-black/30 to-black/70" />
          <div className="absolute bottom-10 left-10 right-10 font-editorial text-4xl leading-tight tracking-tighter text-balance">
            &ldquo;The quiet, obsessive work.
            <br />
            <em className="text-[var(--accent)]">Behind every launch.</em>&rdquo;
          </div>
        </div>
      </div>
    </div>
  );
}
